/* eslint-disable default-case, react/no-did-update-set-state */
import classnames from 'classnames';
import React from 'react';
import { t } from 'es6/services/localization';
import * as Paths from 'es6/core/paths';
import Pagination from 'es6/components/common/pagination';
import StatsMixin from 'es6/components/home/stats-mixin';

const PanelMixin = {
  mixins: [StatsMixin],

  propTypes: {
    aggregateMemories: React.PropTypes.object.isRequired,
    goals: React.PropTypes.array.isRequired,
    onUnenroll: React.PropTypes.func.isRequired,
    onFocus: React.PropTypes.func.isRequired,
    onUnfocus: React.PropTypes.func.isRequired,
    pageSize: React.PropTypes.number,
    selected: React.PropTypes.bool.isRequired,
    type: React.PropTypes.string.isRequired,
  },

  getDefaultProps() {
    return {
      pageSize: 20,
    };
  },

  getInitialState() {
    return {
      page: 1,
    };
  },

  getPanelStyle() {
    return {
      display: this.props.selected ? 'block' : 'none',
    };
  },

  /**
   * Define getStatString for the StatsMixin.
   *
   * @param {String} type
   * @returns {String}
   */
  getStatString(type) {
    switch (type) {
      case 'started':
        return t('course-tabs.started');
      case 'items-zone-1':
        return t('course-tabs.items-zone-1');
      case 'items-zone-2':
        return t('course-tabs.items-zone-2');
      case 'time-studied':
        return t('course-tabs.time-studied');
    }

    return '';
  },

  /**
   * If the component updates, but the page number exceeds the number of
   * items, change the page.
   */
  componentDidUpdate() {
    if (this.state.page > 1 && this.state.page > this.getPageCount()) {
      this.setState({ page: 1 });
    }
  },

  getPageCount() {
    return Math.ceil(this.getFilteredGoals().length / this.props.pageSize);
  },

  /**
   * Filters using searchBy and filterBy. Primary filter needs
   * to happen in the panel.
   */
  filterBy(goal) {
    let filterBy = true;
    let searchBy = true;

    if (this.state.filterBy === 'focused') {
      filterBy = this.props.aggregateMemories[goal.id].focused;
    }

    if (this.state.searchBy) {
      // Replace white space with search for anything in between.
      const searchString = this.state.searchBy.replace(/[\s]+/g, '.+');
      searchBy = goal.title.search(new RegExp(searchString, 'i')) !== -1;
    }

    return filterBy && searchBy;
  },

  filterByChanged(e) {
    this.setState({
      filterBy: e.target.value,
      page: 1,
    });
  },

  handlePageClick(page) {
    this.setState({ page });
  },

  /**
   * Renders the launcher for a goal. If there are less than 10 study items, show it as
   * disabled.
   */
  renderLauncher(goal) {
    const isDisabled = !goal.isStudiable();
    let className = classnames('btn launch', { disabled: isDisabled });
    let path = isDisabled ? null : Paths.studyGoal(goal.id);

    return (
      <section className="content-column launch-app">
        <a href={path} className={className} rel="app">{t('launch')}</a>
      </section>
    );
  },

  /**
   * Helper for rendering goals.
   * Requires a getFilteredGoals() method to be defined.
   * Requires a getNoCoursesMessage() method to be defined.
   * Requires a renderGoal() method to be defined.
   */
  renderGoals() {
    const start = (this.state.page - 1) * this.props.pageSize;
    const end = start + this.props.pageSize;
    const goals = this.getFilteredGoals().slice(start, end);

    if (goals.length === 0) {
      const msg = this.state.searchBy ? t('course-tabs.no-results') : this.getNoCoursesMessage();
      return (<h2 className="empty-courses-notice" dangerouslySetInnerHTML={{ __html: msg }} />);
    } else {
      return goals.map(goal => this.renderGoal(goal));
    }
  },

  /**
   * Helper for rendering pagination.
   * Requires a getFilteredGoals() method to be defined.
   */
  renderPagination() {
    let pageCount = this.getPageCount();

    return (
      <Pagination page={this.state.page} pages={pageCount} onPageClick={this.handlePageClick} />
    );
  },

  renderSearchBox() {
    return (
      <div className="details-secondary">
        <input className="content-field search" id="keyword" maxLength="255" name="keyword" value={this.props.value} onChange={this.searchByChanged} placeholder={t('search-courses-by-title')} type="search" />
      </div>
    );
  },

  searchByChanged(e) {
    this.setState({
      page: 1,
      searchBy: e.target.value,
    });
  },

  /**
   * Uses state sortBy field to sort goals.
   */
  sortBy(goalA, goalB) {
    const aggregateA = this.props.aggregateMemories[goalA.id];
    const aggregateB = this.props.aggregateMemories[goalB.id];

    switch (this.state.sortBy) {
      case 'study-progress':
        return aggregateA.items.progress < aggregateB.items.progress ? 1 : -1;

      // Sort by most recommendationOrder by default.
      default:
        return aggregateA.recommendationOrder > aggregateB.recommendationOrder ? 1 : -1;
    }
  },

  sortByChanged(e) {
    this.setState({
      page: 1,
      sortBy: e.target.value,
    });
  },
};

export default PanelMixin;
